// useDebouncedValue: returns the latest input `value`, but only after the
// caller has stopped changing it for `delayMs` milliseconds. Every write
// resets the timer. Used on numeric inputs (price, quantity, hourly rate,
// yield percent) so a full recalculation only fires 300ms after the last
// keystroke, keeping typing itself snappy.
//
// Also exposes a `flush()` helper that immediately commits the pending
// value, useful for onBlur or save handlers that must not wait for the
// timer to elapse.
import { useEffect, useRef, useState, useCallback } from "react";

export function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(value), delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delayMs]);

  const flush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setDebounced(value);
  }, [value]);

  return [debounced, flush];
}

// useDebouncedCallback: fires `fn` only after `delayMs` has elapsed since
// the last invocation. Use it for onChange handlers that trigger backend
// PATCH calls: onChange stays instant for the UI, the network call only
// fires once the user stops typing.
export function useDebouncedCallback(fn, delayMs = 300) {
  const timerRef = useRef(null);
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);
  return useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => fnRef.current(...args), delayMs);
  }, [delayMs]);
}
